import React from 'react';
import {Link,useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiZap,FiDollarSign,FiSettings,FiUsers,FiShield,FiTrendingUp,FiArrowRight,FiCode,FiBrain,FiTarget}=FiIcons;

const Home=()=> {
  const navigate=useNavigate();

  const fadeInUp={
    initial: {opacity: 0,y: 60},
    animate: {opacity: 1,y: 0},
    transition: {duration: 0.6}
  };

  const staggerContainer={
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const handleStartProject=()=> {
    navigate('/contact');
  };

  const handleLearnMore=()=> {
    navigate('/about');
  };

  const handleExploreServices=()=> {
    navigate('/services');
  };

  const handleViewCaseStudies=()=> {
    navigate('/case-studies');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-20 pb-16 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-6xl font-bold text-gray-900 mb-6"
            >
              <span className="bg-gradient-to-r from-forest-600 to-forest-800 bg-clip-text text-transparent">
                aiximo
              </span>
              <br />
              Unlock Rapid,High-Quality,and Cost-Effective Software Development
            </motion.h1>
            
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-700 mb-8 max-w-4xl mx-auto leading-relaxed"
            >
              We are your specialized AI-powered agency,delivering applications faster,smarter,and more affordably—empowering startups,SMBs,and enterprises to innovate and scale.
            </motion.p>

            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/contact"
                className="bg-gradient-to-r from-forest-600 to-forest-700 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Request a Free Consultation
              </Link>
              <Link
                to="/services"
                className="border-2 border-forest-600 text-forest-700 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-forest-600 hover:text-white transition-all duration-300"
              >
                Explore Our Services
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 40}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Your Vision,Accelerated by AI
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              In today's competitive landscape,the demand for fast,reliable,and cost-efficient software is paramount. At <strong className="text-forest-700">aiximo</strong>,we transcend traditional development,leveraging cutting-edge AI technologies to transform your concepts into powerful applications with unprecedented speed and precision. We exist to solve your biggest pain points: high costs,long development cycles,and the need for uncompromised quality. As a startup,we understand the critical importance of agility and efficiency in bringing innovative ideas to life.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 40}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Partner with aiximo?
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Our unique blend of human expertise and AI-driven efficiency ensures your project's success.
            </p>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{once: true}}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {[
              {
                icon: FiZap,
                title: "Speed to Market",
                description: "Launch your product in record time with AI-driven prototyping,code generation,and accelerated development pipelines. Essential for any startup or enterprise aiming for rapid innovation."
              },
              {
                icon: FiDollarSign,
                title: "Cost Efficiency",
                description: "Drastically reduce development costs and optimize resources. AI automation means you get superior value for your investment,allowing your budget to go further."
              },
              {
                icon: FiSettings,
                title: "Flexible Engagements",
                description: "Choose project-based,retainer,or tiered service packages tailored precisely to your budget and project requirements."
              },
              {
                icon: FiUsers,
                title: "Expert Team",
                description: "Our developers and prompt engineers combine deep AI expertise with rigorous quality assurance,ensuring robust,scalable,and secure applications."
              },
              {
                icon: FiShield,
                title: "Transparent Process",
                description: "Benefit from our agile methodology,regular updates,and clear communication every step of the way,keeping you fully informed and in control."
              },
              {
                icon: FiTrendingUp,
                title: "Future-Proof Solutions",
                description: "Build applications designed for scalability and longevity,with architecture that adapts to evolving business needs and technological advances."
              }
            ].map((feature,index)=> (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-forest-600 to-forest-700 rounded-xl flex items-center justify-center mb-6">
                  <SafeIcon icon={feature.icon} className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-700 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 40}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Specialized AI-Accelerated Services
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              We cover the full spectrum of your development needs,powered by AI.
            </p>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{once: true}}
            variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          >
            {[
              {
                icon: FiCode,
                title: "Rapid Application Development",
                description: "Build bespoke solutions from concept to deployment at unparalleled speed."
              },
              {
                icon: FiBrain,
                title: "AI-Enhanced Code Generation & Optimization",
                description: "Leverage AI for cleaner,more efficient,and high-performing code."
              },
              {
                icon: FiTarget,
                title: "Prompt Engineering & AI Integration",
                description: "Expertly guide AI models to craft precise functionalities and seamlessly integrate AI into your systems."
              },
              {
                icon: FiShield,
                title: "Code Auditing & Refinement",
                description: "Ensure the integrity,security,and maintainability of your existing or new codebases."
              },
              {
                icon: FiSettings,
                title: "Ongoing Maintenance & Support",
                description: "Rely on us for continuous enhancements,updates,and reliable support post-launch."
              }
            ].map((service,index)=> (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="flex items-start space-x-6 p-6 rounded-2xl hover:bg-gray-50 transition-colors duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-forest-600 to-forest-700 rounded-xl flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={service.icon} className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-700">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{opacity: 0,y: 20}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.6,delay: 0.3}}
            className="text-center mt-12"
          >
            <Link
              to="/services"
              className="inline-flex items-center text-forest-700 font-semibold hover:text-forest-800 transition-colors"
            >
              View All Our Services
              <SafeIcon icon={FiArrowRight} className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 40}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our AI-Accelerated Development Process
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8">
              Experience a streamlined journey from idea to launch,designed for efficiency and clarity.
            </p>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{once: true}}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[
              {
                number: "01",
                title: "Discovery",
                description: "We begin by deeply assessing your needs and thoroughly scoping your project,utilizing AI-assisted analysis for precise planning."
              },
              {
                number: "02",
                title: "Prototyping",
                description: "AI tools rapidly generate your initial application mockups and core functionalities,bringing your vision to life in record time."
              },
              {
                number: "03",
                title: "Refinement",
                description: "Our expert developers and prompt engineers meticulously polish and optimize the application for quality,security,and peak performance."
              },
              {
                number: "04",
                title: "Launch & Support",
                description: "We ensure seamless deployment,followed by ongoing maintenance,support,and strategic enhancements to guarantee long-term success."
              }
            ].map((step,index)=> (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-forest-600 to-forest-700 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-lg">{step.number}</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-700">{step.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-forest-700 to-forest-800 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <motion.div
            initial={{opacity: 0,y: 40}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Accelerate Your Development?
            </h2>
            <p className="text-xl text-forest-100 mb-8">
              Let's build the future,together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleStartProject}
                className="bg-white text-forest-700 px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Contact Us for a Free Consultation
              </button>
              <button
                onClick={handleViewCaseStudies}
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white hover:text-forest-700 transition-all duration-300"
              >
                See Our Case Studies
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;